import React, { useState, useRef, useEffect, memo } from 'react';

const OptimizedImage = memo(({ 
  src, 
  srcSet,
  alt, 
  width, 
  height, 
  className,
  style,
  sizes,
  priority = false,
  decoding = 'auto',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(priority); // Priority images start as loaded
  const [isInView, setIsInView] = useState(priority); // Priority images are always "in view"
  const imgRef = useRef(null);

  useEffect(() => {
    // Skip intersection observer for priority (LCP) images
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { 
        rootMargin: '200px', // Load images earlier
        threshold: 0.01 
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    console.warn('Failed to load image:', src);
    setIsLoaded(true); // Remove loading state even on error
  };

  // For priority/LCP images, render immediately without wrapper overhead
  if (priority) {
    return (
      <img
        src={src}
        srcSet={srcSet}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        loading="eager"
        fetchpriority="high"
        decoding="sync"
        onLoad={handleLoad}
        onError={handleError}
        className={className}
        style={{
          ...style,
          width: '100%',
          height: 'auto',
          opacity: 1,
          display: 'block'
        }}
        {...props}
      />
    );
  }

  return (
    <div 
      ref={imgRef}
      className={className}
      style={{
        ...style,
        minHeight: height || 'auto',
        backgroundColor: isLoaded ? 'transparent' : '#1a1a2e',
        transition: 'background-color 0.2s ease'
      }}
    >
      {isInView && (
        <img
          src={src}
          srcSet={srcSet}
          alt={alt}
          width={width}
          height={height}
          sizes={sizes}
          loading="lazy"
          fetchpriority="auto"
          decoding={decoding}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            width: '100%',
            height: 'auto',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.2s ease',
            display: 'block'
          }}
          {...props}
        />
      )}
    </div>
  );
});

OptimizedImage.displayName = 'OptimizedImage';

export default OptimizedImage;